import { httpClient } from '../core/http';
import { secureStorage } from '../utils/secureStorage';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  user: User;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_verified: boolean;
  organization_id: number;
  organization?: Organization;
  created_at: string;
  updated_at: string;
  roles?: string[];
  organization_role?: OrganizationRole | null;
  ds_tokens_balance?: number;
  ds_tokens_total?: number;
}

export type OrganizationRole = 'owner' | 'admin' | 'member';

export interface Organization {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  organization_name?: string;
  organization_description?: string;
  organization_id?: number;
}

export interface RegisterResponse {
  message: string;
  user: User;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
}

export interface UserStats {
  total_users: number;
  active_users: number;
  verified_users: number;
  organizations_count: number;
}

class AuthService {
  private async get<T>(url: string): Promise<T> {
    const response = await httpClient.get<T>(url);
    return response.data;
  }

  private async post<T>(url: string, data?: any): Promise<T> {
    const response = await httpClient.post<T>(url, data);
    return response.data;
  }

  private async put<T>(url: string, data?: any): Promise<T> {
    const response = await httpClient.put<T>(url, data);
    return response.data;
  }

  private async delete<T>(url: string): Promise<T> {
    const response = await httpClient.delete<T>(url);
    return response.data;
  }

  /**
   * Login do usuário
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.post<LoginResponse>(
      '/v1/users/login',
      credentials
    );

    // Salvar token no localStorage
    if (response.access_token) {
      secureStorage.setItem('access_token', response.access_token);
      secureStorage.setItem('token_type', response.token_type);
      secureStorage.setItem('expires_in', response.expires_in.toString());
      secureStorage.setItem('token_created_at', Date.now().toString());
      if (response.refresh_token) {
        secureStorage.setItem('refresh_token', response.refresh_token);
      }
    }

    return response;
  }

  /**
   * Registro de novo usuário
   */
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    return this.post<RegisterResponse>('/v1/users/register', userData);
  }

  /**
   * Logout do usuário
   */
  async logout(): Promise<void> {
    try {
      // Obter refresh_token do localStorage antes de limpar
      const refreshToken = secureStorage.getItem('refresh_token');
      
      // Chamar endpoint de logout no backend com refresh_token
      if (refreshToken) {
        await this.post<void>('/v1/users/logout', {
          refresh_token: refreshToken,
        });
      } else {
        // Se não houver refresh_token, ainda tenta fazer logout
        await this.post<void>('/v1/users/logout');
      }
    } catch (error) {
      // Mesmo se a chamada falhar, continuar com a limpeza local
    } finally {
      secureStorage.removeItem('access_token');
      secureStorage.removeItem('token_type');
      secureStorage.removeItem('expires_in');
      secureStorage.removeItem('token_created_at');
      secureStorage.removeItem('refresh_token');
      secureStorage.removeItem('user');
    }
  }

  /**
   * Obter informações do usuário atual
   */
  async getCurrentUser(): Promise<User> {
    const response = await this.get<User>('/v1/users/me');

    secureStorage.setItem('user', JSON.stringify(response));

    return response;
  }

  /**
   * Obter informações do usuário via endpoint /auth/me
   */
  async getAuthUser(): Promise<User> {
    return this.get<User>('/auth/me');
  }

  /**
   * Obter roles do usuário
   */
  async getUserRoles(): Promise<string[]> {
    return this.get<string[]>('/auth/roles');
  }

  /**
   * Atualizar perfil do usuário
   */
  async updateProfile(profileData: UpdateProfileRequest): Promise<User> {
    const response = await this.put<User>('/v1/users/me', profileData);

    secureStorage.setItem('user', JSON.stringify(response));

    return response;
  }

  /**
   * Solicitar reset de senha
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/v1/users/password-reset', {
      email,
    });
  }

  /**
   * Confirmar reset de senha
   */
  async confirmPasswordReset(
    token: string,
    newPassword: string
  ): Promise<{ message: string }> {
    return this.post<{ message: string }>('/v1/users/password-reset/confirm', {
      token,
      new_password: newPassword,
    });
  }

  /**
   * Listar todos os usuários (admin)
   */
  async listUsers(
    skip: number = 0,
    limit: number = 10
  ): Promise<{ users: User[]; total: number }> {
    return this.get<{ users: User[]; total: number }>(
      `/v1/users/?skip=${skip}&limit=${limit}`
    );
  }

  /**
   * Obter usuário por ID
   */
  async getUserById(id: number): Promise<User> {
    return this.get<User>(`/v1/users/${id}`);
  }

  /**
   * Atualizar usuário por ID (admin)
   */
  async updateUserById(
    id: number,
    userData: UpdateProfileRequest
  ): Promise<User> {
    return this.put<User>(`/v1/users/${id}`, userData);
  }

  /**
   * Deletar usuário (admin)
   */
  async deleteUser(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/v1/users/${id}`);
  }

  /**
   * Obter estatísticas do usuário
   */
  async getUserStats(): Promise<UserStats> {
    return this.get<UserStats>('/v1/users/stats/overview');
  }

  /**
   * Verificar se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    const token = secureStorage.getItem('access_token');
    const expiresIn = secureStorage.getItem('expires_in');
    const tokenCreatedAt = secureStorage.getItem('token_created_at');

    if (!token || !expiresIn || !tokenCreatedAt) {
      return false;
    }

    // Verificar se o token não expirou
    const tokenCreatedTime = parseInt(tokenCreatedAt);
    const tokenDuration = parseInt(expiresIn) * 1000; // Converter para milissegundos
    const expirationTime = tokenCreatedTime + tokenDuration;
    const currentTime = Date.now();

    return currentTime < expirationTime;
  }

  /**
   * Obter token de acesso
   */
  getAccessToken(): string | null {
    return secureStorage.getItem('access_token');
  }

  /**
   * Obter dados do usuário do localStorage
   */
  getStoredUser(): User | null {
    const userStr = secureStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Verificar se o email está verificado
   */
  isEmailVerified(): boolean {
    const user = this.getStoredUser();
    return user?.is_verified || false;
  }

  /**
   * Renovar token de acesso
   */
  async refreshToken(): Promise<LoginResponse> {
    return this.post<LoginResponse>('/v1/users/refresh-token');
  }

  /**
   * Verificar se o token está próximo do vencimento (últimos 5 minutos)
   */
  isTokenNearExpiry(): boolean {
    const expiresIn = secureStorage.getItem('expires_in');
    const tokenCreatedAt = secureStorage.getItem('token_created_at');

    if (!expiresIn || !tokenCreatedAt) {
      return true;
    }

    const tokenCreatedTime = parseInt(tokenCreatedAt);
    const tokenDuration = parseInt(expiresIn) * 1000;
    const expirationTime = tokenCreatedTime + tokenDuration;
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    // Considerar próximo do vencimento se restam menos de 5 minutos
    return timeUntilExpiry < 5 * 60 * 1000;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; message: string }> {
    return this.get<{ status: string; message: string }>('/health');
  }
}

export const authService = new AuthService();
